/**
 * Toast system — provides a simple notification primitive for the app.
 *
 * Usage:
 *   1. Mount <ToastHost /> once at the app root (inside ToastProvider).
 *   2. Wrap your tree in <ToastProvider>.
 *   3. Call useToast() in any component to get push({ kind, message }) helper.
 *
 * Toasts auto-dismiss after 3 seconds.
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import styles from "./Toast.module.scss";

export type ToastKind = "success" | "error" | "info";

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  toasts: Toast[];
  push: (toast: Omit<Toast, "id">) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((toast: Omit<Toast, "id">) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, push, dismiss }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Soft fail: return a no-op API rather than crashing if provider is missing.
    return {
      toasts: [],
      push: () => undefined,
      dismiss: () => undefined
    };
  }
  return ctx;
}

export function ToastHost() {
  const { toasts, dismiss } = useToast();

  return (
    <div className={styles.host} aria-live="polite" aria-atomic="true">
      {toasts.map((t) => (
        <ToastItem
          key={t.id}
          toast={t}
          onDismiss={() => dismiss(t.id)}
        />
      ))}
    </div>
  );
}

interface ToastItemProps {
  toast: Toast;
  onDismiss: () => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3500);
    return () => clearTimeout(t);
  }, [onDismiss]);

  const icon = toast.kind === "success" ? "✓" : toast.kind === "error" ? "✕" : "ⓘ";
  const className = `${styles.toast} ${styles[toast.kind]}`;
  return (
    <div className={className} role="status" onClick={onDismiss}>
      <span className={styles.icon}>{icon}</span>
      <span className={styles.message}>{toast.message}</span>
    </div>
  );
}
